import { Box, Button, Stack } from '@mui/material'
import {
  addCareArticleEngagementMetric,
  addLearnArticleEngagementMetric,
  addLessonEngagementMetric,
} from 'api/metrics'
import { useAuthContext } from 'components/context/AuthProvider'
import { ArticleHeader } from 'components/drawer/ArticleHeader'
import FailPlaceholder from 'components/placeholders/FailPlaceholder'
import React, { useCallback, useMemo } from 'react'
import { useMetric } from 'services/Metric'
import {
  ArticleType,
  ETimelinePostStatus,
  IMetricScope,
  IMetricType,
  LearnArticle,
  Lesson,
  ShareTimelineItem,
} from 'types'
import { formatMetricDate } from 'utils'
import { ArticleAudioUserView } from '../../../Article/components/ArticleAudioUserView/ArticleAudioUserView'
import { ArticlePreview } from '../../../Article/components/ArticlePreview/ArticlePreview'

export function SharedArticleDrawerContent({
  data,
  type,
  handleClose,
  onUpdateStatus,
  onChangeRoute,
}: {
  data: ShareTimelineItem
  type: ArticleType
  handleClose: () => void
  onUpdateStatus: (status: ETimelinePostStatus, isSilent: boolean) => void
  onChangeRoute: (route: string) => void
}) {
  const { user } = useAuthContext()

  const onClose = useCallback(() => {
    handleClose()
  }, [handleClose])

  const articleItem = useMemo(() => {
    if (data.lessonId !== null) {
      return data.lesson
    }

    if (data.careArticleId !== null) {
      return data.careArticle
    }

    if (data.learnArticleId !== null) {
      return data.learnArticle
    }
  }, [data])

  const careArticleType = useMemo(() => {
    return data.careArticle ? data.careArticle.type : null
  }, [data])

  const metricOptions = useMemo(() => {
    if (data.lessonId !== null) {
      return { type: IMetricType.lesson, scope: IMetricScope.lesson }
    }

    if (data.learnArticleId !== null) {
      return { type: IMetricType.learnArticle, scope: IMetricScope.learnArticle }
    }

    if (data.careArticleId !== null) {
      return { type: IMetricType.careArticle, scope: IMetricScope.careArticle }
    }

    return { type: undefined, scope: undefined }
  }, [data])

  const handleSaveMetric = useCallback(
    async (value: number, uuid: string, scope: IMetricScope) => {
      if (!user) {
        return
      }
      if (!articleItem) return

      if (type === ArticleType.Lesson) {
        await addLessonEngagementMetric({
          lessonUuid: uuid,
          lessonId: data.id,
          courseId: (articleItem as Lesson).courseId,
          userId: user.id,
          engagedFrom: formatMetricDate(value),
          engagedTo: formatMetricDate(),
          scope,
        })
      } else if (type === ArticleType.LearnLibrary) {
        await addLearnArticleEngagementMetric({
          userId: user.id,
          learnArticleId: data.id,
          learnTopicId: (articleItem as LearnArticle).topicId,
          scope,
          engagedFrom: formatMetricDate(value),
          engagedTo: formatMetricDate(),
        })
      } else if (type === ArticleType.CareArticle) {
        await addCareArticleEngagementMetric({
          userId: user.id,
          careArticleId: data.id,
          scope,
          engagedFrom: formatMetricDate(value),
          engagedTo: formatMetricDate(),
        })
      }
    },
    [data, type, user, articleItem],
  )

  useMetric({
    type: metricOptions.type,
    scope: metricOptions.scope,
    entityId: data.id,
    hasIdleTimer: true,
    idleLimitMin: 2,
    onSave: handleSaveMetric,
  })

  const onReviewLater = useCallback(() => {
    onUpdateStatus(ETimelinePostStatus.review, false)
  }, [onUpdateStatus])

  const onDone = useCallback(() => {
    onUpdateStatus(ETimelinePostStatus.done, false)
  }, [onUpdateStatus])

  if (!articleItem) {
    return <FailPlaceholder error="Problem with article" />
  }

  return (
    <>
      <ArticleHeader
        type={type}
        careArticleType={careArticleType}
        date={articleItem.createdAt}
        articleTitle={articleItem.post || articleItem.name}
        handleClose={onClose}
      />
      <ArticleAudioUserView
        enMediaUrl={articleItem.enMediaUrl}
        enMediaFileMimeType={articleItem.enMediaFile?.mimeType}
        esMediaUrl={articleItem.esMediaUrl}
        esMediaFileMimeType={articleItem.esMediaFile?.mimeType}
        metricType={metricOptions.type}
        metricEntityId={articleItem.id}
        onSaveMetric={handleSaveMetric}
      />
      <Box mt={2} mb={2} fontSize={20} minHeight="128px">
        <ArticlePreview
          data={articleItem.article}
          metricType={metricOptions.type}
          metricEntityId={data.id}
          onSaveVideoMetric={handleSaveMetric}
        />
      </Box>

      <Stack direction="row" justifyContent="space-between" spacing={1}>
        <Stack direction="row" spacing={1} justifyContent="flex-end"></Stack>
        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button variant="contained" onClick={onReviewLater}>
            Continue Later
          </Button>
          <Button variant="contained" onClick={onDone}>
            Done
          </Button>
        </Stack>
      </Stack>
    </>
  )
}
